/**
 * Active and archived chats for the hub and the wall's Chats pin: today's
 * server search, 50 per page, by project; their row menu and how a row opens.
 */
import { useCallback, useEffect } from "react";
import {
  renameDurableConversation,
  type DurableConversation,
} from "@/api/conversations";
import {
  conversationActionAllowed,
  conversationIsRunning,
} from "@/components/panels/conversation-status";
import { useConversationBrowser } from "@/hooks/use-conversation-browser";
import { translate } from "@/i18n";
import { panelsSearchMessages } from "@/i18n/messages/panels-search";
import { openDurableConversationFrame } from "@/lib/open-durable-conversation-frame";
import {
  activeExecutionForSession,
  stopActiveExecutionForSession,
} from "@/lib/stop-agent-session";
import { useAgentStore } from "@/stores/agent-store";
import { useNotificationStore } from "@/stores/notification-store";
import type { HubMenuAction } from "./hub-types";

export interface HubChats {
  active: DurableConversation[];
  archived: DurableConversation[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadingMore: boolean;
  loadMore: () => Promise<void>;
  archivedHasMore: boolean;
  archivedLoadingMore: boolean;
  loadMoreArchived: () => Promise<void>;
  /** The chat's “…” menu: Stop generating (`canStopChat`), Rename…, then Archive or Unarchive. */
  menu: (
    chat: DurableConversation,
    onRename: (chat: DurableConversation) => void,
  ) => HubMenuAction[];
  rename: (chat: DurableConversation, name: string) => Promise<void>;
}

/** The chat's runtime session: the live foreground one, else its latest segment's. */
function chatRuntimeSessionId(chat: DurableConversation): string | null {
  return (
    chat.runtime?.foreground_runtime_session_id ??
    chat.frame_runtime_segment?.runtime_session_id ??
    null
  );
}

/** Running, and a stop can target exactly one execution of its runtime session. */
function canStopChat(chat: DurableConversation): boolean {
  const sessionId = chatRuntimeSessionId(chat);
  return (
    conversationIsRunning(chat) &&
    sessionId !== null &&
    activeExecutionForSession(sessionId, chat.project_id) !== null
  );
}

function stopChat(chat: DurableConversation): void {
  const sessionId = chatRuntimeSessionId(chat);
  if (sessionId) stopActiveExecutionForSession(sessionId, chat.project_id);
}

/** A chat row opens the chat's frame, as in the hub; `closePopover` runs first. */
export function openHubChat(
  chat: DurableConversation,
  closePopover: () => void,
): void {
  void openDurableConversationFrame({
    conversation: chat,
    addToast: useNotificationStore.getState().addSystemToast,
    closePopover,
    selectAgent: useAgentStore.getState().selectAgent,
  });
}

export function useHubChats({
  query,
  projectId,
  archived = true,
}: {
  query: string;
  projectId: string | null;
  /** False (the wall's Chats pin): archived chats are neither listed nor searched. */
  archived?: boolean;
}): HubChats {
  const activeBrowser = useConversationBrowser({ projectId });
  const archivedBrowser = useConversationBrowser({
    initialArchiveState: "archived",
    projectId,
    enabled: archived,
  });
  const {
    setSearchQuery: setActiveQuery,
    mutateArchive: archiveChat,
    refresh: refreshActive,
  } = activeBrowser;
  const {
    setSearchQuery: setArchivedQuery,
    mutateArchive: restoreChat,
    refresh: refreshArchived,
  } = archivedBrowser;

  useEffect(() => {
    setActiveQuery(query);
    setArchivedQuery(query);
  }, [query, setActiveQuery, setArchivedQuery]);

  const menu = useCallback(
    (
      chat: DurableConversation,
      onRename: (chat: DurableConversation) => void,
    ): HubMenuAction[] => {
      const archived = Boolean(chat.archived_at);
      const action = archived ? "unarchive" : "archive";
      const stop: HubMenuAction[] = canStopChat(chat)
        ? [
            {
              id: "stop",
              label: translate(panelsSearchMessages, "stopGenerating"),
              onSelect: () => stopChat(chat),
            },
          ]
        : [];
      // Archive or Unarchive only where the server allows it: hidden, not
      // disabled.
      const archiving: HubMenuAction[] = conversationActionAllowed(
        chat,
        action,
        true,
      )
        ? [
            {
              id: action,
              label: translate(panelsSearchMessages, action),
              onSelect: async () => {
                if (archived) {
                  await restoreChat(chat);
                  await refreshActive();
                } else {
                  await archiveChat(chat);
                  await refreshArchived();
                }
              },
            },
          ]
        : [];
      return [
        ...stop,
        {
          id: "rename",
          label: translate(panelsSearchMessages, "rename"),
          onSelect: () => onRename(chat),
        },
        ...archiving,
      ];
    },
    [archiveChat, refreshActive, refreshArchived, restoreChat],
  );

  const rename = useCallback(
    async (chat: DurableConversation, name: string) => {
      await renameDurableConversation(chat, name);
      await Promise.all([refreshActive(), refreshArchived()]);
    },
    [refreshActive, refreshArchived],
  );

  return {
    active: activeBrowser.rows,
    archived: archivedBrowser.rows,
    loading: activeBrowser.listState === "loading",
    error: activeBrowser.listError,
    hasMore: activeBrowser.hasMore,
    loadingMore: activeBrowser.loadingMore,
    loadMore: activeBrowser.loadMore,
    archivedHasMore: archivedBrowser.hasMore,
    archivedLoadingMore: archivedBrowser.loadingMore,
    loadMoreArchived: archivedBrowser.loadMore,
    menu,
    rename,
  };
}
